import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { UKMap } from "@/components/UKMap";
import { getFarms } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function Farms() {
    const [farms, setFarms] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        getFarms()
            .then((f) => {
                setFarms(f);
                if (f.length) setSelectedId(f[0].id);
            })
            .catch(() => setFarms([]));
    }, []);

    return (
        <main className="bg-[#0B0B0C] text-[#F6F1E7] min-h-screen pt-28 sm:pt-36 pb-24" data-testid="farms-page">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                <Reveal className="text-center mb-16">
                    <p className="eyebrow text-[#C9B37A] mb-4">Provenance</p>
                    <h1 className="font-serif-display text-5xl sm:text-6xl font-medium">Our Farms</h1>
                    <p className="mt-5 text-sm text-white/50 max-w-lg mx-auto leading-relaxed">
                        Five farming families. Five native breeds. Every batch we make can be traced back to one of these
                        farms — and to the people who raised it.
                    </p>
                </Reveal>

                {!farms ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-80 rounded-xl bg-white/[0.05]" />
                        ))}
                    </div>
                ) : (
                    <>
                        <Reveal className="mb-20">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 sm:p-12">
                                <div>
                                    <UKMap farms={farms} selectedId={selectedId} onSelect={setSelectedId} />
                                    <div className="flex flex-wrap justify-center gap-2 mt-6" data-testid="farms-map-chips">
                                        {farms.map((farm) => (
                                            <button
                                                key={farm.id}
                                                onClick={() => setSelectedId(farm.id)}
                                                className={`eyebrow px-4 py-2 border rounded-full transition-colors duration-200 ${
                                                    selectedId === farm.id
                                                        ? "bg-[#C9B37A] text-[#0B0B0C] border-[#C9B37A]"
                                                        : "border-white/20 text-white/60 hover:border-[#C9B37A] hover:text-[#C9B37A]"
                                                }`}
                                                data-testid={`farms-chip-${farm.id}`}
                                            >
                                                {farm.region}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    {farms
                                        .filter((f) => f.id === selectedId)
                                        .map((farm) => (
                                            <div key={farm.id} data-testid="farms-selected-card">
                                                <p className="eyebrow text-[#C9B37A]">{farm.region}</p>
                                                <h2 className="font-serif-display text-4xl font-medium mt-2">{farm.name}</h2>
                                                <p className="text-sm text-white/55 mt-4 leading-relaxed">{farm.story}</p>
                                                <Link
                                                    to={`/farms/${farm.id}`}
                                                    className="btn-luxe btn-gold mt-7"
                                                    data-testid="farms-view-selected-button"
                                                >
                                                    Visit {farm.name.split(" ")[0]} <ArrowRight size={13} />
                                                </Link>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {farms.map((farm, i) => (
                                <Reveal key={farm.id} delay={Math.min(i * 0.08, 0.3)}>
                                    <Link
                                        to={`/farms/${farm.id}`}
                                        className="group block rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.03] hover:border-[#C9B37A]/40 transition-colors duration-300"
                                        data-testid={`farm-card-${farm.id}`}
                                    >
                                        <div className="h-56 overflow-hidden">
                                            <img
                                                src={farm.images[0]}
                                                alt={farm.name}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-7">
                                            <div className="flex items-baseline justify-between gap-4">
                                                <h3 className="font-serif-display text-2xl font-medium">{farm.name}</h3>
                                                <span className="font-mono-meta text-[10px] tracking-wide text-[#C9B37A]">
                                                    EST. {farm.established}
                                                </span>
                                            </div>
                                            <p className="font-mono-meta text-[10px] tracking-wide text-white/45 mt-2">
                                                {farm.region.toUpperCase()} · {farm.breed.toUpperCase()} · {farm.farmer_name.toUpperCase()}
                                            </p>
                                            <p className="text-sm text-white/55 mt-4 leading-relaxed line-clamp-2">{farm.story}</p>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </main>
    );
}
